import GooglePayButton from '@google-pay/button-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IS_DEV } from '../../../shared/constants';
import { PORTMONE_MERCHANT_ID } from '../../../shared/env.config';
import { debug } from '../../../shared/lib/general';
import { EVENT_NAMES, GooglePaySheetCloseReason, useSendEventLog } from '../../event-log';
import {
    EMERCHANT_PAY_AUTH_METHODS,
    EMERCHANT_PAY_GATEWAY,
    GOOGLE_PAY_AUTH_METHODS,
    GOOGLE_PAY_SUPPORTED_NETWORKS,
    MERCHANT_NAME,
    TRANZZO_PAY_GATEWAY,
} from '../payment.constants';

const isSheetCloseReason = (code: unknown): code is GooglePaySheetCloseReason =>
    typeof code === 'string' && code in EVENT_NAMES.googlePaySheetClosed;

/** Google's `PaymentsError.statusCode`, or `UNKNOWN` for an error without one. */
const resolveSheetCloseReason = (error: unknown): GooglePaySheetCloseReason => {
    const code = typeof error === 'object' && error !== null && 'statusCode' in error ? error.statusCode : undefined;
    return isSheetCloseReason(code) ? code : 'UNKNOWN';
};

interface GooglePayBtnProps {
    orderAmount: number;
    currencyCode: string;
    onPaymentCompleted: (response: google.payments.api.PaymentData) => void;
    onError: (err?: unknown) => void;
    innerClassName?: string;
    gateway: string;
    merchantId: string;
    isSandbox?: boolean;
}

const GooglePayBtn = (props: GooglePayBtnProps) => {
    const { t } = useTranslation();
    const {
        orderAmount,
        currencyCode,
        innerClassName = '',
        onPaymentCompleted,
        onError,
        gateway,
        merchantId,
        isSandbox = false,
    } = props;
    // Tranzzo sandbox still needs real Google Pay tokens (it rejects TEST
    // tokens), so it always runs the PRODUCTION environment. Every other gateway
    // keeps the original dev/sandbox → TEST rule untouched.
    const isTranzzo = gateway === TRANZZO_PAY_GATEWAY;
    const environment = isTranzzo || (!IS_DEV && !isSandbox) ? 'PRODUCTION' : 'TEST';
    const allowedAuthMethods =
        gateway === EMERCHANT_PAY_GATEWAY ? EMERCHANT_PAY_AUTH_METHODS : GOOGLE_PAY_AUTH_METHODS;
    const { sendEvent } = useSendEventLog();

    return (
        <div
            onClick={() => {
                sendEvent({ name: EVENT_NAMES.paymentInitiated.Google });
            }}
        >
            <GooglePayButton
                className={`button_text kmg ${innerClassName}`}
                buttonType="plain"
                environment={environment}
                paymentRequest={{
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                        {
                            type: 'CARD',
                            parameters: {
                                allowedAuthMethods: [...allowedAuthMethods],
                                allowedCardNetworks: [...GOOGLE_PAY_SUPPORTED_NETWORKS],
                                // Tells PAN_ONLY (no cardholder authentication → 3DS) from a
                                // device token before the commit; the server logs it (SWRN-2089).
                                assuranceDetailsRequired: isTranzzo ? true : undefined,
                            },
                            tokenizationSpecification: {
                                type: 'PAYMENT_GATEWAY',
                                parameters: {
                                    gateway: gateway,
                                    gatewayMerchantId: merchantId,
                                },
                            },
                        },
                    ],
                    merchantInfo: {
                        merchantId: PORTMONE_MERCHANT_ID,
                        merchantName: MERCHANT_NAME,
                    },
                    transactionInfo: {
                        totalPriceStatus: 'FINAL',
                        totalPriceLabel: t('Total') as string,
                        totalPrice: `${orderAmount}`,
                        currencyCode: currencyCode,
                        countryCode: 'UA',
                    },
                }}
                onLoadPaymentData={(paymentRequest) => {
                    debug('load payment data', 'data', paymentRequest);
                    onPaymentCompleted(paymentRequest);
                }}
                onCancel={(reason) => {
                    // Closing the sheet keeps the buyer on the payment screen, as before.
                    sendEvent({ name: EVENT_NAMES.googlePaySheetClosed[resolveSheetCloseReason(reason)] });
                }}
                onError={(error) => {
                    debug('Google Pay error', 'error', error);
                    sendEvent({ name: EVENT_NAMES.googlePaySheetClosed[resolveSheetCloseReason(error)] });
                    onError(error);
                }}
            />
        </div>
    );
};

const GooglePayBtnMemo = React.memo(GooglePayBtn);
export { GooglePayBtnMemo as GooglePayBtn };
